import { useTranslation } from 'react-i18next'
import { Box, Stack, Typography, useTheme } from '@mui/material'
import { Site, BuildingEquipment } from '__generated__/graphql'

interface BuildingData {
  name: string
  equipment: BuildingEquipment
}

export interface IPopupPropsContent {
  site: Site
  color: string
  buildingData?: BuildingData
  notSupervised: boolean
}

const getBackgroundColor = (status, theme): string => {
  const statusMap: { [key: string]: string } = {
    'up': '#E6F0D7',
    'degraded': '#FFF4E5',
    'down': '#FFEBEE',
    'notSupervised': theme.palette.grey[200],
  }
  return statusMap[status] || theme.palette.grey[200]
}

const PopupContent = ({
  site,
  color,
  buildingData,
  notSupervised,
}: IPopupPropsContent) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const displayData = buildingData
    ? {
        name: buildingData.name,
        nb_accesspoints: (buildingData.equipment?.accesspoint_ok ?? 0) + (buildingData.equipment?.accesspoint_unreachable ?? 0),
        nb_accesspoints_up: buildingData.equipment?.accesspoint_ok ?? 0,
      }
    : {
        name: site.name,
        nb_accesspoints: site.equipment?.nb_accesspoints ?? 0,
        nb_accesspoints_up: site.equipment?.nb_accesspoints_ok ?? 0,
      }

  let status = site.equipment?.available_level ?? 'down'

  if (buildingData) {
    status = buildingData.equipment?.available_level ?? 'notReferenced'
  }

  if (notSupervised) {
    status = 'notSupervised'
  }

  return (
    <Stack spacing={1.5} alignItems="center">
      <Box
        sx={{
          display: 'inline-flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '16px',
          backgroundColor: getBackgroundColor(status, theme),
          borderRadius: '8px',
          px: 1,
          width: 'fit-content',
        }}
      >
        <Typography
          sx={{
            color: color,
            fontSize: '10px',
            fontWeight: 500,
            lineHeight: '16px',
            fontFamily: 'Poppins',
            textTransform: 'capitalize',
          }}
        >
          {t(`cartography.maps.equipment.available_level.${status}`)}
        </Typography>
      </Box>

      <Typography
        sx={{
          fontSize: '12px',
          fontWeight: 500,
          lineHeight: '16px',
          fontFamily: 'Poppins',
          color: '#292929',
          textAlign: 'center',
        }}
      >
        {displayData.name}
      </Typography>

      {!notSupervised && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ width: '100%' }}
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <i
              className="fa-light fa-signal-stream"
              style={{
                color: theme.palette.primary.main,
                fontSize: '14px',
                fontWeight: 400,
              }}
            />
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: '16px',
                fontFamily: 'Poppins',
                color: theme.palette.text.primary,
                whiteSpace: 'nowrap',
              }}
            >
              {t('cartography.maps.popup.operational_stations')}
            </Typography>
          </Stack>

          <Box
            sx={{
              flex: 1,
              height: '1px',
              backgroundColor: '#F7F7F7',
            }}
          />

          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: '16px',
              fontFamily: 'Poppins',
              color: theme.palette.text.primary,
              whiteSpace: 'nowrap',
            }}
          >
            {displayData.nb_accesspoints_up}/<span style={{ color: '#989898' }}>{displayData.nb_accesspoints}</span>
          </Typography>
        </Stack>
      )}
    </Stack>
  )
}

export default PopupContent
